import WorkspaceService from "@core-ui/ContextServices/Workspace";
import {
	createContext,
	type MutableRefObject,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { applyHomeScroll, enterHomeScrollWorkspace, type HomeScreen, saveHomeScroll } from "./homeScrollMemory";

type HomeScroll = { container: HTMLDivElement; activeKey: MutableRefObject<string | null> };

export const HomeScrollContext = createContext<HomeScroll | null>(null);

export const useHomeScrollOwner = () => {
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const activeKey = useRef<string | null>(null);
	const workspacePath = WorkspaceService.current()?.path;

	// a home screen with no positions of its own (a workspace without catalogs) still leaves the previous workspace
	useLayoutEffect(() => enterHomeScrollWorkspace(workspacePath), [workspacePath]);

	// LegacyScrollShadowContainer re-attaches its ref on every render: a passed-through null would blink the context
	const ref = useCallback((element: HTMLDivElement | null) => {
		if (element) setContainer(element);
	}, []);

	useEffect(() => {
		if (!container) return;
		const save = () => {
			if (activeKey.current) saveHomeScroll(activeKey.current, container.scrollTop);
		};
		container.addEventListener("scroll", save, { passive: true });
		return () => container.removeEventListener("scroll", save);
	}, [container]);

	const scroll = useMemo(() => (container ? { container, activeKey } : null), [container]);

	return { ref, scroll };
};

export const useHomeScrollPosition = (href: string, screen: HomeScreen) => {
	const scroll = useContext(HomeScrollContext);
	const workspacePath = WorkspaceService.current()?.path;

	useLayoutEffect(() => {
		if (!scroll) return;
		scroll.activeKey.current = href;
		applyHomeScroll(scroll.container, workspacePath, href, screen);
	}, [scroll, workspacePath, href, screen]);
};
